import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import GroupTable from './groupTable';
import Paginate from './groupsPaginate';

const useStyles = makeStyles({
  scrollBar: {
    '&::-webkit-scrollbar': {
      width: '1.3em',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#212121',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#5e5e5e',
      borderRadius: '7px',
      border: '3px solid #212121',
    },
  },
});

const GroupsSection = props => {
  const { groupsInfo = {}, paletteType, displayGroupMode, displayGroup, displaySkybox } = props;
  const classes = useStyles();
  const [groups, setGroups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [groupsPerPage, setGroupsPerPage] = useState(5);
  const [groupAuditTrailRecords, setGroupAuditTrailRecords] = useState([]);

  function getLastRecordPerGroup(auditTrailRecords) {
    const lastRecordPerGroup = {};

    if (!auditTrailRecords) return lastRecordPerGroup;

    for (let i = 0; i < auditTrailRecords.length; i += 1) {
      const record = auditTrailRecords[i];
      const groupId = record.killshot_group_id;

      const isSkybox = record.actor_id === 'SKYBOX';
      const shouldSkip = !displaySkybox && isSkybox;

      if (!shouldSkip) {
        const existing = lastRecordPerGroup[groupId];
        const currentDate = Date.parse(record.created_on);

        if (!existing || Date.parse(existing.created_on) < currentDate) {
          lastRecordPerGroup[groupId] = record;
        }
      }
    }

    return lastRecordPerGroup;
  }

  const groupLastRecordObject = getLastRecordPerGroup(groupsInfo);

  function lastAuditRecordPerGroupObject(originalAuditTrail, lastRecordPerGroupObject) {
    const newDataObject = {};

    if (!originalAuditTrail || !lastRecordPerGroupObject) return originalAuditTrail;

    for (let i = 0; i < originalAuditTrail.length; i += 1) {
      const record = originalAuditTrail[i];
      const groupId = record.killshot_group_id;

      const isSkybox = record.actor_id === 'SKYBOX';
      const shouldSkip = !displaySkybox && isSkybox;

      if (!shouldSkip) {
        const last = lastRecordPerGroupObject[groupId];

        if (last && last.group_name === record.group_name && last.created_on === record.created_on) {
          if (!newDataObject[groupId]) newDataObject[groupId] = [];
          newDataObject[groupId].push({ ...record });
        }
      }
    }

    return newDataObject;
  }

  const groupLastAuditRecordObject = lastAuditRecordPerGroupObject(groupsInfo, groupLastRecordObject);

  function sortData(providedData) {
    let sortedData = [];

    if (Object.values(providedData)) {
      sortedData = Object.values(providedData).sort((a, b) => {
        const keyA = new Date(a[0].created_on);
        const keyB = new Date(b[0].created_on);
        if (keyA > keyB) {
          return -1;
        }
        if (keyA < keyB) {
          return 1;
        }
        return 0;
      });
    }
    return sortedData;
  }

  function orderGroupAuditRecords(groupAuditRecords) {
    let sortedData = [];

    if (groupAuditRecords) {
      sortedData = groupAuditRecords.sort((record1, record2) => {
        const keyA = new Date(record1.created_on);
        const keyB = new Date(record2.created_on);
        if (keyA > keyB) {
          return 1;
        }
        if (keyA < keyB) {
          return -1;
        }
        return 0;
      });
    }
    return sortedData;
  }

  function groupRecords() {
    if (groupsInfo) {
      if (groupsInfo.length > 0) {
        setGroupAuditTrailRecords(groupsInfo.filter(record => record.group_name === displayGroup));
      }
    }
  }

  const sortedData = sortData(groupLastAuditRecordObject);

  useEffect(() => {
    setGroups(sortedData);
    groupRecords(displayGroup);
  }, [groupsInfo, displayGroup]);

  const indexOfLastGroup = currentPage * groupsPerPage;
  const indexOfFirstGroup = indexOfLastGroup - groupsPerPage;
  if (indexOfFirstGroup > groups.length) {
    setCurrentPage(currentPage - 1);
  }
  const currentGroups = groups.slice(indexOfFirstGroup, indexOfLastGroup);

  const previousPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage !== Math.ceil(groups.length / groupsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const changeGroupsPerPage = numberOfGroups => {
    setGroupsPerPage(numberOfGroups);
  };

  return (
    <div>
      <Box
        mb={2}
        display="flex"
        flexDirection="column"
        height="500px"
        className={paletteType === 'dark' && classes.scrollBar}
        style={{
          overflow: 'hidden',
          overflowY: 'auto',
          scrollbarColor: '#2b2b2b',
        }}
      >
        {displayGroup === '' ? (
          <div>
            {currentGroups.map(groupInfo => (
              <GroupTable
                groupsInfo={groupInfo}
                key={groupInfo.created_on}
                paletteType={paletteType}
                displayGroupMode={displayGroupMode}
                displayGroup={displayGroup}
              />
            ))}
          </div>
        ) : (
          <div>
            <GroupTable
              groupsInfo={orderGroupAuditRecords(groupAuditTrailRecords)}
              key={displayGroup}
              paletteType={paletteType}
              displayGroupMode={displayGroupMode}
              displayGroup={displayGroup}
            />
          </div>
        )}
      </Box>
      {displayGroup === '' && (
        <Paginate
          currentPage={currentPage}
          groupsPerPage={groupsPerPage}
          totalPosts={groups.length}
          previousPage={previousPage}
          nextPage={nextPage}
          changeGroupsPerPage={changeGroupsPerPage}
        />
      )}
      <hr
        style={{
          'background-color': 'lightgray',
          height: '1px',
          border: '0',
        }}
      />
    </div>
  );
};

GroupsSection.propTypes = {
  groupsInfo: PropTypes.array.isRequired,
  paletteType: PropTypes.string.isRequired,
  displayGroupMode: PropTypes.func.isRequired,
  displayGroup: PropTypes.string.isRequired,
  displaySkybox: PropTypes.bool.isRequired,
};

export default GroupsSection;
